import { useCallback } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useSecurityPin } from "@/shared/hooks/useSecurityPin";
import { stepUpMethodOf } from "@/shared/utils/stepUp";
import { twoFactorRefusalMessage } from "@/shared/utils/twoFactorErrors";

/**
 * Turns a refused step-up code into the sentence its challenge shows (see `twoFactorRefusalMessage`) and
 * re-reads the step-up state: that refusal may have just locked the method — the authenticator and the PIN
 * both lock after five wrong codes — and the challenge shows the lock and its countdown from the user it
 * reads.
 */
export function useStepUpRefusal(): (error: unknown, fallback: string) => string {
  const { user } = useAuth();
  const { refresh } = useSecurityPin();
  const hasMethod = stepUpMethodOf(user) !== null;

  return useCallback(
    (error: unknown, fallback: string) => {
      if (hasMethod) void refresh();
      return twoFactorRefusalMessage(error, fallback);
    },
    [hasMethod, refresh],
  );
}
